import { useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";

interface Props { licenseNumber: string; }

export default function Enrich({ licenseNumber }: Props) {
  const { context } = useSDK();
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [territory, setTerritory] = useState("");
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post(context, "/api/enrich", {
        license_number: licenseNumber,
        brand_supplied_website: website || undefined,
        brand_supplied_name: name || undefined,
        territory: territory || undefined,
      });
      setResult(r);
    } catch (err: any) {
      setResult({ status: "error", notes: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h2 className="text-xl font-semibold">Enrich retailer record</h2>
      <p className="text-sm text-slate-600 mb-4">
        The license <code>{licenseNumber}</code> is confirmed at the DCC, but we're missing consumer-facing data (address, place ID, or DBA).
        Supply whatever you know — we'll run Google Places, attach a place_id, and backfill our record.
      </p>

      <div className="card p-4 space-y-3">
        <div>
          <label className="text-sm font-medium">Consumer-facing name</label>
          <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1" placeholder="e.g. Rose & Green Retail" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">Website</label>
          <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1" placeholder="https://…" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>
        <div>
          <label className="text-sm font-medium">Territory / city (helps Places search)</label>
          <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1" placeholder="e.g. Los Angeles" value={territory} onChange={(e) => setTerritory(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-primary" disabled={busy || (!name && !website)} onClick={go}>
            {busy ? "Looking up…" : "Run reverse-Places lookup"}
          </button>
          <a className="btn-ghost" href="#/review">Back to review</a>
        </div>
      </div>

      {result && (
        <div className="card p-4 mt-4 text-sm">
          <div className="font-semibold mb-1">Result: {result.status}</div>
          {result.status === "enriched" && (
            <>
              <div><span className="text-slate-500">Place:</span> {result.place_name}</div>
              <div className="text-slate-500 text-xs">{result.place_address}</div>
              <div className="text-slate-500 text-xs">{result.notes}</div>
              <a className="btn-primary mt-3 inline-block" href="#/review">Back to review</a>
            </>
          )}
          {result.status === "no_places_match" && (
            <>
              <div className="text-slate-600">{result.notes}</div>
              <p className="mt-2">You can still record what you entered — the link will remain suggested.</p>
            </>
          )}
          {result.status === "disabled" && (
            <div className="text-amber-700">Enrichment is disabled: {result.notes}</div>
          )}
          {result.status === "error" && (
            <div className="text-red-600">{result.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}
